<template>
  <div :class="[$options.name, { mobile: isMobile }, { disabled: busy }]">
    <template v-if="!submitted">
      <h1 class="tw:heading-1" style="font-weight: 400; margin: 0 0 0.25rem">
        {{ passwordAction === "new_account_password" ? translations["new-account"].title : translations.title }}
      </h1>
      <p style="font-size: 1rem; font-weight: 300; margin: 0 0 2rem">
        {{ passwordAction === "new_account_password" ? translations["new-account"].subtitle : translations.subtitle }}
      </p>
      <EdroneInput
        id="password-input"
        :class="[
          'required-inputs',
          'tw:mb-4',
          {
            'tw:mb-2': v$.userNewPassword.$dirty && v$.userNewPassword.regex.$invalid,
          },
        ]"
        :error-msg="
          v$.userNewPassword.$dirty && v$.userNewPassword.$error
            ? translations.inputs.password.errors[v$.userNewPassword.$errors[0].$message]
            : null
        "
        :invalid="
          v$.userNewPassword.$dirty && (v$.userNewPassword.required.$invalid || v$.userNewPassword.regex.$invalid)
        "
        :label="
          passwordAction === 'new_account_password'
            ? translations['new-account'].inputs.password.label
            : translations.inputs.password.label
        "
        :value="userNewPassword"
        type="password"
        unique-id="978cf071-1a2b-45ac-b685-ea5b5e8d66e5"
        @change="checkValidation(v$.userNewPassword)"
        @input="handlePasswordInput"
        @keyup.enter="handleNextInput"
      />
      <div
        v-if="v$.userNewPassword.$dirty && v$.userNewPassword.regex.$invalid"
        style="margin: 0 0 1.25rem; width: 100%; font-size: 0.875rem; color: #686868"
      >
        <template v-for="(message, index) in errorMessages" :key="index">
          <p :class="['tw:m-0', { valid: message.isValid }]">{{ message.isValid ? "✓" : "✗" }} {{ message.text }}</p>
        </template>
      </div>
      <EdroneInput
        :error-msg="
          v$.userNewPasswordConfirm.$dirty && v$.userNewPasswordConfirm.$error
            ? translations.inputs.confirm.errors[v$.userNewPasswordConfirm.$errors[0].$message]
            : null
        "
        :invalid="
          v$.userNewPasswordConfirm.$dirty &&
          (v$.userNewPasswordConfirm.required.$invalid || v$.userNewPasswordConfirm.sameAs.$invalid)
        "
        :label="
          passwordAction === 'new_account_password'
            ? translations['new-account'].inputs.confirm.label
            : translations.inputs.confirm.label
        "
        :value="userNewPasswordConfirm"
        class="required-inputs tw:mb-6"
        type="password"
        unique-id="05323fda-35b5-4a6b-8aec-3392fb643a4f"
        @change="checkValidation(v$.userNewPasswordConfirm)"
        @input="handlePasswordConfirmInput"
        @keyup.enter="handleNextInput"
      />
      <EdroneButton
        :disabled="
          userNewPassword && userNewPasswordConfirm && !v$.userNewPassword.$error && !v$.userNewPasswordConfirm.$error
            ? false
            : true
        "
        class="required-inputs tw:mb-4"
        style="width: 100%"
        type="solid"
        unique-id="69a6c5f2-e2e7-497f-9bbf-2c2b53f18bc0"
        @click="printResult"
      >
        {{ translations.buttons.confirm }}
      </EdroneButton>
      <EdroneButton type="text" unique-id="b8adf06c-19bf-4144-b365-89902b06d116" @click="handleBack">
        <template #left-icon>
          <font-awesome-icon icon="fa-solid fa-chevron-left" />
        </template>
        {{ translations.buttons.back }}
      </EdroneButton>
    </template>
    <template v-else>
      <h1 class="tw:heading-1" style="font-weight: 400; margin: 0 0 0.25rem">
        <font-awesome-icon icon="fa-light fa-circle-check" style="color: #00c672" />
        {{ translations.submitted.title }}
      </h1>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, sameAs, helpers } from "@vuelidate/validators";
import axios from "vue-app/utils/axios";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneInput from "edrone-components/EdroneInput.vue";

export default defineComponent({
  name: "AccountResetPasswordForm",
  components: {
    EdroneButton,
    EdroneInput,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    passwordAction: {
      type: String,
      default: "",
    },
  },
  setup() {
    const translations = window._trans._account["password-management"].reset;
    const submitted = ref(false);

    const userNewPassword = ref("");
    const userNewPasswordConfirm = ref("");

    const regex = helpers.regex(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?=.{8,})/);
    // symbols :  !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~

    const rules = {
      userNewPassword: {
        required,
        regex: helpers.withMessage("Invalid Password", regex),
      },
      userNewPasswordConfirm: { required, sameAs: sameAs(userNewPassword) },
    };
    const v$ = useVuelidate(rules, { userNewPassword, userNewPasswordConfirm });

    function handlePasswordInput(value: string) {
      userNewPassword.value = value;
      returnInvalidPasswordDetails(value);
    }

    function handlePasswordConfirmInput(value: string) {
      userNewPasswordConfirm.value = value;
    }

    const errorMessages = [
      {
        isValid: false,
        text: translations.inputs.password.errors["Lowercase Character"],
      },
      {
        isValid: false,
        text: translations.inputs.password.errors["Uppercase Character"],
      },
      { isValid: false, text: translations.inputs.password.errors["Digit"] },
      { isValid: false, text: translations.inputs.password.errors["Symbol"] },
      {
        isValid: false,
        text: translations.inputs.password.errors["8 characters long"],
      },
    ];

    function returnInvalidPasswordDetails(value: string) {
      errorMessages.forEach((message) => {
        message.isValid = true;
      });
      if (!/^(?=.*[a-z])/.test(value)) {
        // Lowercase Character
        errorMessages[0].isValid = false;
      }
      if (!/^(?=.*[A-Z])/.test(value)) {
        // Uppercase Character
        errorMessages[1].isValid = false;
      }
      if (!/^(?=.*\d)/.test(value)) {
        // Digit
        errorMessages[2].isValid = false;
      }
      if (!/^(?=.*\W)/.test(value)) {
        // Symbol
        errorMessages[3].isValid = false;
      }
      if (!/^(?=.{8,})/.test(value)) {
        // 8 characters long
        errorMessages[4].isValid = false;
      }
    }

    function handleNextInput(event: KeyboardEvent) {
      const inputs = document.querySelectorAll(".required-inputs input, .required-inputs.edrone-button");
      const index = Array.from(inputs).indexOf(event.target as HTMLElement);
      if (index === 0) {
        v$.value.userNewPassword.$touch();
      }
      if (index === 1) {
        v$.value.userNewPasswordConfirm.$touch();
      }
      (inputs[index + 1] as HTMLElement)?.focus();
      if (index === 1 && !!userNewPassword.value && !!userNewPasswordConfirm.value) {
        printResult();
      }
    }

    const busy = ref<boolean>(false);

    function printResult() {
      v$.value.userNewPassword.$touch();
      v$.value.userNewPasswordConfirm.$touch();

      if (!v$.value.userNewPassword.$error && !v$.value.userNewPasswordConfirm.$error) {
        resetPassword();
      }
    }

    async function resetPassword() {
      try {
        busy.value = true;
        const params = new Proxy(new URLSearchParams(window.location.search), {
          get: (searchParams, prop) => searchParams.get(prop),
        });

        let bodyFormData = new FormData();
        bodyFormData.append("reset_password_token", params.reset_password_token);
        bodyFormData.append("email", params.email);
        bodyFormData.append("_password", userNewPassword.value);
        bodyFormData.append("_confirmation_password", userNewPasswordConfirm.value);

        const response = await axios({
          method: "post",
          url: "/reset_password",
          data: bodyFormData,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.status === "success") {
          submitted.value = true;
          window.location.href = "/login/trackers";
        } else {
          busy.value = false;
        }
      } catch (error) {
        busy.value = false;
      }
    }

    function checkValidation(validator) {
      validator.$touch();
    }

    function handleBack() {
      window.location.href = "/login";
    }

    return {
      printResult,
      errorMessages,
      submitted,
      busy,
      translations,
      userNewPassword,
      userNewPasswordConfirm,
      handlePasswordInput,
      handlePasswordConfirmInput,
      v$,
      handleNextInput,
      returnInvalidPasswordDetails,
      checkValidation,
      handleBack,
    };
  },
});
</script>

<style lang="scss" scoped>
.valid {
  color: #00c672;
}
</style>
